import { type FunctionalComponent, h, Ref, VNode, withDirectives } from "vue";
import DSIcon from "@/src/design_system/primitives/icon/components/DSIcon.vue";
import { DS_ICON_APPEARANCES, DS_ICON_SIZES } from "@/src/design_system/primitives/icon/types";
import type { DSIconName } from "@/src/design_system/primitives/icon/types/iconName.generated";
import { DS_OPTION_LABEL_CLASSES } from "@/src/design_system/form_elements/shared_modules/option_label/constants/classes";
import type { DSChoiceOption } from "@/src/design_system/form_elements/shared/types/option";
import { vDsTooltip } from "@/src/design_system/primitives/tooltip/directives/vDsTooltip";
import type { DSOptionLabelSlots } from "@/src/design_system/form_elements/shared_modules/option_label/types/slots";
import { hasSlotContent } from "@/src/shared/utils/slots";

type LabelSlots = Pick<DSOptionLabelSlots, "label">;

export const useRenderLabelContent = (option: Ref<DSChoiceOption>, tooltip: Ref<string | undefined>) => {
  const hasLabel = (slots: LabelSlots) => !!option.value.label || hasSlotContent(slots, "label");
  const shouldWrapWithContent = (slots: LabelSlots) =>
    hasLabel(slots) && (option.value.icon || option.value.description);

  const getIconNode = (): VNode | null => {
    return option.value.icon
      ? h(DSIcon, {
          icon: option.value.icon as DSIconName,
          appearance: DS_ICON_APPEARANCES.Outlined,
          size: DS_ICON_SIZES.Small,
        })
      : null;
  };

  const getLabelTextNode = (slots: LabelSlots): VNode | null => {
    if (!hasLabel(slots)) return null;

    const labelNode = h(
      "span",
      {
        class: DS_OPTION_LABEL_CLASSES.Label,
        "data-cy": "ds-option-label__label",
      },
      hasSlotContent(slots, "label") ? slots.label?.() : [option.value.label]
    );

    return withDirectives(labelNode, [[vDsTooltip, tooltip.value]]);
  };

  const getDescriptionTextNode = (): VNode | null => {
    return option.value.description
      ? h("span", { class: DS_OPTION_LABEL_CLASSES.Description }, [option.value.description])
      : null;
  };

  const getLabelWithDescriptionNode = (slots: LabelSlots): VNode | null => {
    const labelText = getLabelTextNode(slots);
    const descriptionText = getDescriptionTextNode();

    return labelText && descriptionText
      ? h("span", { class: DS_OPTION_LABEL_CLASSES.Text }, [labelText, descriptionText])
      : labelText;
  };

  // Slots arrive as this functional component's children: changes to the parent's slots alone would not re-render it.
  const renderLabelContent: FunctionalComponent<object, never[], LabelSlots> = (_props, { slots }) => {
    const children = [];

    const iconNode = getIconNode();
    const labelNode = getLabelWithDescriptionNode(slots);

    if (iconNode) children.push(iconNode);
    if (labelNode) children.push(labelNode);

    return shouldWrapWithContent(slots) ? h("span", { class: DS_OPTION_LABEL_CLASSES.Content }, children) : children;
  };

  return { renderLabelContent };
};
